import { Component, type ErrorInfo, type ReactNode } from 'react';
import { buildErrorReport } from '@/lib/buildErrorReport';
import { reportClientError, newTraceId } from '@/api/clientErrors';
import { paths } from '@/constants';
import { FailureScreen } from './FailureScreen';
import { offersDashboardExit } from './failureScreenExits';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When this value changes, the boundary auto-resets.
   *
   * Bug-hunt #96: a sticky `hasError: true` state used to outlive the route
   * that caused it. Pass `location.pathname` (or any value that changes when
   * the user navigates) so the boundary resets cleanly on navigation. For
   * the common in-router case, use `<RouteResettingErrorBoundary>` instead.
   */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /**
   * Trace id for this crash. Minted once in `componentDidCatch` and used
   * for BOTH the telemetry report and the user-facing FailureScreen, so the
   * id the user can quote matches the id on the opsalert alert.
   */
  traceId: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      traceId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Mint the trace id once here and use it for both the telemetry
    // report and the rendered FailureScreen — see ErrorBoundaryState.traceId.
    const traceId = newTraceId();
    this.setState({ errorInfo, traceId });
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    // Ship the crash to vingapi so it surfaces as an opsalert alert.
    // Fire-and-forget — `reportClientError` never throws.
    reportClientError({
      error,
      source: 'boundary',
      componentStack: errorInfo.componentStack ?? undefined,
      traceId,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null, errorInfo: null, traceId: null });
    }
  }

  handleTryAgain = () => {
    window.location.reload();
  };

  handleDashboard = () => {
    // Hard navigation — the boundary wraps the whole router, so a known-good
    // full reload of a safe route is the reliable escape.
    window.location.assign(paths.root);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const report = buildErrorReport({
        title: 'Application Error',
        message: this.state.error?.message || 'Unknown error',
        source: 'client',
        error: this.state.error ?? undefined,
        componentStack: this.state.errorInfo?.componentStack || undefined,
        traceId: this.state.traceId ?? undefined,
      });

      return (
        <FailureScreen
          traceId={this.state.traceId ?? ''}
          report={report}
          onTryAgain={this.handleTryAgain}
          onDashboard={offersDashboardExit() ? this.handleDashboard : undefined}
        />
      );
    }

    return this.props.children;
  }
}
